// @ts-nocheck
import { useEffect, useState } from 'react';
import { Lock, Maximize2, Minimize2, X } from 'lucide-react';
import { StatusBadge } from '../shared/StatusBadge';
import { VOTE_ROOM_SLIDE_DEFAULT_WIDTH, VOTE_ROOM_SLIDE_MAXIMIZED_WIDTH } from '../shared/voteRoomConstants';

/**
 * Right-edge slide-over panel shared by the View-Activity and Preview-Vote-Room flyouts. Adds a
 * maximize/restore toggle and an Encrypted + optional status badge in the header — features the
 * generic `SlideOverPanel` doesn't yet expose.
 */
export const VoteRoomSlideOver = ({ open, onClose, title, titleId, subtitle, statusVariant, children, footer }) => {
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    if (!open) setMaximized(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const widthClass = maximized ? VOTE_ROOM_SLIDE_MAXIMIZED_WIDTH : VOTE_ROOM_SLIDE_DEFAULT_WIDTH;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} aria-hidden />
      <div
        className={`absolute top-0 right-0 flex h-full flex-col bg-white shadow-xl transition-[width,max-width] duration-200 ease-out ${widthClass}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="min-w-0 pr-3">
            <div className="flex flex-wrap items-center gap-2">
              <h3 id={titleId} className="text-base font-medium text-gray-900">
                {title}
              </h3>
              <StatusBadge variant="secure">
                <Lock size={8} className="mr-1" aria-hidden />
                Encrypted
              </StatusBadge>
              {statusVariant ? <StatusBadge variant={statusVariant}>{statusVariant}</StatusBadge> : null}
            </div>
            {subtitle ? (
              <p className="mt-0.5 truncate text-xs text-gray-500" title={subtitle}>
                {subtitle}
              </p>
            ) : null}
          </div>
          <div className="flex flex-shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={() => setMaximized((v) => !v)}
              className="rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label={maximized ? 'Restore panel size' : 'Maximize panel'}
              aria-pressed={maximized}
              title={maximized ? 'Restore' : 'Maximize'}
            >
              {maximized ? <Minimize2 size={18} aria-hidden /> : <Maximize2 size={18} aria-hidden />}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <X size={20} aria-hidden />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>
        {footer ? (
          <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-gray-50/40 px-5 py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};
